import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
    createUser, 
    validateEmail, 
    validatePassword,
    handleUserManagementError
  } from "@/services/userManagement";
import { UserPlus, Mail, Lock, X } from "lucide-react";

const CreateUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const [formData, setFormData] = useState({
    userEmail: "",
    password: "",
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    userEmail: "",
    password: "",
  });

  const validateForm = () => {
    const errors = {
      userEmail: "",
      password: "",
    };

    if (!formData.userEmail) {
      errors.userEmail = "Email is required";
    } else if (!validateEmail(formData.userEmail)) {
      errors.userEmail = "Please enter a valid email address";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.message;
      }
    }

    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsCreating(true);
    setError("");

    try {
      await createUser({ ...formData, userEmail: formData.userEmail.toLowerCase() });

      setFormData({
        userEmail: "",
        password: "",
      });

      onUserCreated();
      onClose();
    } catch (err) {
      console.error("Error creating user:", err);
      const message = err instanceof Error ? err.message : "Failed to create user";
      if (message.toLowerCase().includes("exists") || message.toLowerCase().includes("already")) {
        setError("A user with this email already exists.");
      } else {
        setError(message);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border-slate-200/50 shadow-2xl">
        <CardHeader className="border-b border-slate-200/50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              <span>Create New User</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Email */}
            <div className="space-y-2">
              <Label htmlFor="userEmail" className="text-sm font-medium text-slate-700 flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>User Email *</span>
              </Label>
              <Input
                id="userEmail"
                type="email"
                value={formData.userEmail}
                onChange={(e) => handleInputChange("userEmail", e.target.value)}
                placeholder="user@example.com"
                className={`${validationErrors.userEmail ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-200 focus:border-blue-500 focus:ring-blue-500"}`}
              />
              {validationErrors.userEmail && (
                <p className="text-red-500 text-sm">{validationErrors.userEmail}</p>
              )}
            </div>



            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 flex items-center space-x-2">
                <Lock className="h-4 w-4" />
                <span>Password *</span>
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Enter password"
                className={`${validationErrors.password ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-200 focus:border-blue-500 focus:ring-blue-500"}`}
              />
              {validationErrors.password && (
                <p className="text-red-500 text-sm">{validationErrors.password}</p>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-700 text-xs font-medium mb-1">Password Requirements:</p>
                <ul className="text-blue-600 text-xs space-y-1">
                  <li>• At least 8 characters long</li>
                  <li>• One uppercase letter (A-Z)</li>
                  <li>• One lowercase letter (a-z)</li>
                  <li>• One number (0-9)</li>
                  <li>• One special character (!@#$%^&*)</li>
                </ul>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose} 
                disabled={isCreating}
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isCreating}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateUserModal;
